/**
 * Sentry related utilities
 */
import * as Sentry from "@sentry/vue";
import { BrowserTracing } from "@sentry/tracing";
import { App } from "vue";
import { Router } from "vue-router";
import { backendEnvName } from "@/shared/utils/amplify";
import { useAuthStore } from "@/auth/store";

function sentryScope() {
  // Hub always has a scope in the browser SDK
  return Sentry.getCurrentHub().getScope()!;
}

function setSentryUser(email: string | null, groups?: string[]) {
  if (email) {
    Sentry.setUser({ email, username: email });
    sentryScope().setTag(
      "user.groups",
      groups?.length ? groups.join(",") : ""
    );
  } else {
    Sentry.setUser(null);
    sentryScope().setTag("user.groups", "");
  }
}

/** Configure sentry on the provided vuejs app */
export function configureSentry(app: App<unknown>, router: Router) {
  Sentry.init({
    app,
    dsn: "https://5a1b4ffffdbc4b55a90c3f2fb944159d@sentry.papernest.com/16",
    release: process.env.RELEASE,
    integrations: [
      new BrowserTracing({
        routingInstrumentation: Sentry.vueRouterInstrumentation(router, {
          routeLabel: "name",
        }),
        tracingOrigins: [
          "localhost",
          "https://dev.paperoffice.ops.papernest.com",
          "https://staging.paperoffice.ops.papernest.com",
          "https://paperoffice.ops.papernest.com",
          // All Amplify API Gateway backends share this domain regardless of environment/subdomain
          /\.execute-api\.eu-west-3\.amazonaws\.com/,
        ],
      }),
    ],
    tracesSampleRate: 1.0,
    trackComponents: true,
    environment: backendEnvName,
  });

  router.beforeEach((_to, from) => {
    sentryScope().setTag("previous.route", (from.name as string) ?? "");
  });

  router.afterEach(() => {
    const auth = useAuthStore();
    setSentryUser(auth.email, auth._groups);
  });
}
